/** Carry the anchor across a link to a domain the customer declared. */

import { state, debugLog } from "./state.js";
import { getExternalId } from "./cookies.js";
import { CROSS_DOMAIN_PARAM, decorateHref } from "./cross-domain-links.js";

/**
 * A funnel that lives on two domains is one journey, and cookies are not.
 *
 * A landing page on `campaign.nl` that sends the visitor to `shop.nl` to buy is
 * two visitors to us: the anchor cookie is scoped to the domain it was set on,
 * so the second one arrives with nothing and the sale has no source. That is
 * not an edge case in this market; ClickFunnels, Kajabi and every "buy" button
 * pointing at a separate checkout domain does exactly this.
 *
 * A DECLARATION, NEVER AN AUTOMATIC. Decorating every outgoing link would hand
 * a visitor id to every third party the customer links to, from their bank to
 * their competitor. So it happens only for hosts the customer listed, and an
 * empty list is a script that does nothing at all.
 *
 * THREE LAYERS, because this one fails quietly. A redirect that rebuilds the
 * URL drops the parameter, a popup opened with `rel="noopener"` never gets it,
 * and a form POST does not carry it either. The parameter is the first layer,
 * `tst_em` from the e-mail links is the second, and the server-side stitch on
 * the address hash is the third and the only one nobody can break. What we do
 * NOT do is bridge on IP: behind one office router that folds strangers
 * together, which is the failure this product is built to avoid.
 */

/**
 * The anchor a visitor arrived with, if a declared domain sent them.
 *
 * Removed from the address bar afterwards: it identifies a visitor, and leaving
 * it there puts it in every screenshot, every shared link and every referrer
 * header the next page sends.
 */
export function readArrivingAnchor(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(CROSS_DOMAIN_PARAM);
    if (!value) return null;

    params.delete(CROSS_DOMAIN_PARAM);
    const query = params.toString();
    const clean = window.location.pathname + (query ? "?" + query : "") + window.location.hash;
    history.replaceState(null, "", clean);

    return value;
  } catch {
    return null;
  }
}

/**
 * Decorate outgoing links to the declared hosts.
 *
 * On the capture phase of a click on `document`, like the rest of the DOM
 * listeners here: a site whose own handler stops propagation would otherwise
 * make this silently never run.
 */
export function installCrossDomainLinks(): void {
  const hosts = state.config?.crossDomainHosts ?? [];
  if (hosts.length === 0) return;

  document.addEventListener(
    "click",
    (event) => {
      try {
        const target = event.target as Element | null;
        const link = target?.closest?.("a[href]") as HTMLAnchorElement | null;
        if (!link) return;

        const anchor = getExternalId();
        const decorated = decorateHref(
          link.getAttribute("href") ?? "",
          hosts,
          anchor,
          window.location.href,
        );
        if (!decorated) return;

        link.setAttribute("href", decorated);
        debugLog("crossDomain", "decorated a link to " + link.hostname);
      } catch {
        /* never let this break somebody else's click handler */
      }
    },
    true,
  );
}
